import React, { useEffect } from "react";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";

const LineChart = ({ data }) => {
  useEffect(() => {
    // Create root element
    const root = am5.Root.new("linechartdiv");

    const myTheme = am5.Theme.new(root);

    // Move minor label a bit down
    myTheme.rule("AxisLabel", ["minor"]).setAll({
      dy: 1,
    });

    // Tweak minor grid opacity
    myTheme.rule("Grid", ["minor"]).setAll({
      strokeOpacity: 0.08,
    });

    // Set themes
    root.setThemes([am5themes_Animated.new(root), myTheme]);

    // Create chart
    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelX: "panX",
        wheelY: "zoomX",
        paddingLeft: 0,
      })
    );

    // Add cursor
    const cursor = chart.set(
      "cursor",
      am5xy.XYCursor.new(root, {
        behavior: "zoomX",
      })
    );
    cursor.lineY.set("visible", false);

    // Your data

    // Format the data for the chart (convert date to timestamp)
    const formattedData = data?.map((item) => ({
      date: new Date(item.date).getTime(),
      appointments: item.totalAppointments,
      earnings: item.totalEarning,
    }));

    // Create axes for Appointments
    const xAxis = chart.xAxes.push(
      am5xy.DateAxis.new(root, {
        maxDeviation: 0,
        baseInterval: {
          timeUnit: "day",
          count: 1,
        },
        renderer: am5xy.AxisRendererX.new(root, {
          minorGridEnabled: true,
          minGridDistance: 200,
          minorLabelsEnabled: true,
        }),
        tooltip: am5.Tooltip.new(root, {}),
      })
    );

    const yAxisAppointments = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {}),
      })
    );

    // Create secondary Y-axis for Earnings
    const yAxisEarnings = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, { opposite: true }),
        extraMin: 0.1, // Adjust scale if necessary
      })
    );

    // Appointments Series
    const appointmentsSeries = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: "Total Appointments",
        xAxis: xAxis,
        yAxis: yAxisAppointments,
        valueYField: "appointments",
        valueXField: "date",
        stroke: am5.color("#EA580C"),
        tooltip: am5.Tooltip.new(root, {
          labelText: "{valueY} Appointments",
        }),
      })
    );

    appointmentsSeries.bullets.push(() => {
      const bulletCircle = am5.Circle.new(root, {
        radius: 5,
        fill: appointmentsSeries.get("fill"),
      });
      return am5.Bullet.new(root, {
        sprite: bulletCircle,
      });
    });

    // Earnings Series
    const earningsSeries = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: "Total Earnings",
        xAxis: xAxis,
        yAxis: yAxisEarnings,
        valueYField: "earnings",
        valueXField: "date",
        stroke: am5.color("#34D399"), // Different color for earnings
        tooltip: am5.Tooltip.new(root, {
          labelText: "{valueY} Earnings",
        }),
      })
    );

    earningsSeries.bullets.push(() => {
      const bulletCircle = am5.Circle.new(root, {
        radius: 5,
        fill: earningsSeries.get("fill"),
      });
      return am5.Bullet.new(root, {
        sprite: bulletCircle,
      });
    });

    // Add scrollbar
    chart.set(
      "scrollbarX",
      am5.Scrollbar.new(root, {
        orientation: "horizontal",
      })
    );

    // Set data to both series
    appointmentsSeries.data.setAll(formattedData);
    earningsSeries.data.setAll(formattedData);

    // Make stuff animate on load
    appointmentsSeries.appear(1000);
    earningsSeries.appear(1000);
    chart.appear(1000, 100);

    // Cleanup function
    return () => {
      root.dispose();
    };
  }, [data]);

  return <div id="linechartdiv" style={{ width: "100%", height: "400px" }} />;
};

export default LineChart;
